import { Injectable } from '@angular/core';
import { BackendService } from './backend.service';
import { Observable, of, throwError } from 'rxjs';
import { RawMaterialGrades } from '../../models/data/rawMaterialGrades.model';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

interface CacheEntry {
  data: RawMaterialGrades;
  expiration: number;
}

@Injectable({ providedIn: 'root' })
export class RawMaterialGradesService {
  private cacheKeyPrefix = 'rawMaterialGrades';
  private ttl = 30 * 60 * 1000; // 30 minutes

  // track in-flight requests to deduplicate
  private inFlight = new Map<string, Observable<RawMaterialGrades>>();

  constructor(private backendService: BackendService) {}

  getRawMaterialGradesFromNormGradesAndLimitations(
    normGradeIds: number[],
    gradeCustomerAnalysisLimitation: string | null,
    gradeCustomerOtherLimitation: string | null
  ): Observable<RawMaterialGrades> {
    const cacheKey = this.generateCacheKey(
      normGradeIds,
      gradeCustomerAnalysisLimitation,
      gradeCustomerOtherLimitation
    );

    // 1) Serve from cache if valid
    const cached = this.getFromCache(cacheKey);
    if (cached) return of(cached);

    // 2) If an identical request is already in-flight, reuse it
    const existing = this.inFlight.get(cacheKey);
    if (existing) return existing;

    // 3) Otherwise start a single HTTP request and share it
    const request$ = this.backendService
      .getRawMaterialGradesFromNormGradesAndLimitations(
        normGradeIds,
        gradeCustomerAnalysisLimitation,
        gradeCustomerOtherLimitation
      )
      .pipe(
        tap(data => this.saveToCache(cacheKey, data)),
        shareReplay(1),
        catchError(err => {
          // ensure future calls can retry
          this.inFlight.delete(cacheKey);
          return throwError(() => err);
        }),
        finalize(() => {
          // clean up when completed successfully
          this.inFlight.delete(cacheKey);
        })
      );

    this.inFlight.set(cacheKey, request$);
    return request$;
  }

  private generateCacheKey(
    normGradeIds: number[],
    gradeCustomerAnalysisLimitation: string | null,
    gradeCustomerOtherLimitation: string | null
  ): string {
    const sortedIds = [...normGradeIds].sort((a, b) => a - b);
    const normalizedAnalysis = gradeCustomerAnalysisLimitation ?? 'null';
    const normalizedOther = gradeCustomerOtherLimitation ?? 'null';
    return `${this.cacheKeyPrefix}-${sortedIds.join(',')}|${normalizedAnalysis}|${normalizedOther}`;
  }

  private getFromCache(key: string): RawMaterialGrades | null {
    const cachedEntry = localStorage.getItem(key);
    if (!cachedEntry) return null;

    const parsed: CacheEntry = JSON.parse(cachedEntry);
    if (parsed.expiration > Date.now()) return parsed.data;

    localStorage.removeItem(key); // expired
    return null;
  }

  private saveToCache(key: string, data: RawMaterialGrades): void {
    const entry: CacheEntry = { data, expiration: Date.now() + this.ttl };
    localStorage.setItem(key, JSON.stringify(entry));
  }
}
